'use client';

import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { forwardRef } from 'react';
import { Divider, Typography, styled } from '@mui/material';
import { useThemeProps } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import type { Options, Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { CodeBlock } from './CodeBlocks';

export interface MarkdownProps extends Omit<Options, 'components'> {
    className?: string;
    markdownContainerClass?: string;
    components?: Components;
}

const MarkdownRoot = styled('div', {
    name: 'MuiMarkdown',
    slot: 'root',
})(({ theme }) => ({
    color: theme.palette.text.primary,
}));

/** @deprecated Use scoped styles on the `varnish-markdown` container instead */
export const TableWrapper = styled('div')`
    overflow-x: auto;
    margin: ${({ theme }) => theme.spacing(3)} 0;

    table {
        width: 100%;
        font-family: ${({ theme }) => theme.typography.fontFamily};
        border: 1px solid ${({ theme }) => theme.palette.grey.A200};

        th,
        td {
            padding: ${({ theme }) => theme.spacing(1.5)};
            border-color: ${({ theme }) => theme.palette.grey.A200};
            border-width: 1px 0;
        }

        th {
            padding-top: ${({ theme }) => theme.spacing(1.5)};
            border-width: 0 0 2px 0;
            white-space: pre;
        }

        tbody tr {
            transition: all 0.3s;
            &:hover {
                background: rgba(60, 90, 100, 0.04);
            }
        }
    }
`;

const Img = styled('img')`
    max-width: 100%;
    &[src*='#thumbnail'] {
        width: ${({ theme }) => theme.spacing(8)};
    }
`;

const Ol = styled('ol')`
    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Ul = styled('ul')`
    margin-bottom: ${({ theme }) => theme.spacing(2)};
    list-style-type: revert;
`;

const Li = styled('li')`
    margin-bottom: 0;
`;

const defaultRemarkPlugins = [remarkGfm];

const defaultComponents: Components = {
    h1: (props: PropsWithChildren) => (
        <Typography gutterBottom variant="h1">
            {props.children}
        </Typography>
    ),
    h2: (props: PropsWithChildren) => (
        <Typography gutterBottom variant="h2">
            {props.children}
        </Typography>
    ),
    h3: (props: PropsWithChildren) => (
        <Typography gutterBottom variant="h3">
            {props.children}
        </Typography>
    ),
    h4: (props: PropsWithChildren) => (
        <Typography gutterBottom variant="h4">
            {props.children}
        </Typography>
    ),
    h5: (props: PropsWithChildren) => (
        <Typography gutterBottom variant="h5">
            {props.children}
        </Typography>
    ),
    h6: (props: PropsWithChildren) => (
        <Typography gutterBottom variant="h6">
            {props.children}
        </Typography>
    ),
    p: (props: PropsWithChildren) => <Typography variant="body1">{props.children}</Typography>,
    b: (props: PropsWithChildren) => (
        <strong style={{ fontWeight: 'bold' }}>{props.children}</strong>
    ),
    em: (props: PropsWithChildren) => <em style={{ fontStyle: 'italic' }}>{props.children}</em>,
    hr: () => <Divider />,
    del: (props: PropsWithChildren) => (
        <del style={{ textDecoration: 'line-through' }}>{props.children}</del>
    ),
    li: (props: ComponentProps<typeof Li>) => <Li {...props} />,
    ul: (props: ComponentProps<typeof Ul>) => <Ul {...props} />,
    ol: (props: ComponentProps<typeof Ol>) => <Ol {...props} />,
    img: (props: ComponentProps<typeof Img>) => <Img {...props} />,
    // CodeBlock renders its own wrapper, so pre is neutralized to avoid double-wrapping
    pre: (props: ComponentProps<'span'>) => <span {...props} />,
    code: ({ children, className }: { children?: ReactNode; className?: string }) => {
        const match = /language-(\w+)/.exec(className || '');
        const isInline = !match;
        if (isInline) {
            return <CodeBlock inline>{children}</CodeBlock>;
        }
        return <CodeBlock language={match[1]}>{children}</CodeBlock>;
    },
    table: (props: ComponentProps<'table'>) => (
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        <TableWrapper>
            <table {...props} />
        </TableWrapper>
    ),
};

const Markdown = forwardRef<HTMLDivElement, MarkdownProps>(function Markdown(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiMarkdown' });
    const {
        markdownContainerClass = 'varnish-markdown',
        className,
        children,
        components = defaultComponents,
        remarkPlugins = defaultRemarkPlugins,
        ...other
    } = props;

    const containerClassName = [markdownContainerClass, className].filter(Boolean).join(' ');

    return (
        <MarkdownRoot ref={ref} className={containerClassName}>
            <ReactMarkdown components={components} remarkPlugins={remarkPlugins} {...other}>
                {children}
            </ReactMarkdown>
        </MarkdownRoot>
    );
});

export { defaultComponents, defaultRemarkPlugins, Markdown, Markdown as default };
